import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { brl, num, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Ship, Package, Clock, AlertTriangle } from "lucide-react";

export default function Imports() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/imports/planning").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="space-y-4"><Skeleton className="h-8 w-64 bg-slate-800" /><Skeleton className="h-96 bg-slate-800" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50 flex items-center gap-2"><Ship className="h-7 w-7 text-rose-400" /> Planejamento de Importação</h1>
          <p className="text-slate-400 text-sm">Peças importadas (China) que precisam entrar nos próximos pedidos.</p>
        </div>
        <div className="flex gap-3">
          <Card className="card-metal px-5 py-3"><div className="text-xs uppercase text-slate-500 font-mono">Itens</div><div className="font-display text-2xl font-bold text-slate-50">{data.total_itens}</div></Card>
          {data.finance && <Card className="card-metal px-5 py-3"><div className="text-xs uppercase text-slate-500 font-mono">Custo estimado</div><div className="font-display text-2xl font-bold text-emerald-300">{brl(data.total_custo_estimado)}</div></Card>}
        </div>
      </div>

      <Card className="card-metal p-0 overflow-hidden">
        <div className="bg-rose-950/30 border-b border-rose-900/40 px-5 py-3 font-display text-lg font-semibold text-rose-200">Próximo Pedido da China</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
              <th className="text-left px-4 py-3">Peça</th><th className="text-left px-4 py-3">Fornecedor</th><th className="text-right px-4 py-3">Estoque</th>
              <th className="text-right px-4 py-3">Lead time</th><th className="text-center px-4 py-3">Status</th><th className="text-left px-4 py-3">Data pedido</th>
              <th className="text-right px-4 py-3">Qtd rec.</th>{data.finance && <th className="text-right px-4 py-3">Custo est.</th>}
            </tr></thead>
            <tbody>
              {data.itens.map((p) => (
                <tr key={p.id} data-testid={`import-row-${p.id}`} className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer" onClick={() => navigate(`/pecas/${p.id}`)}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded bg-slate-950 overflow-hidden shrink-0">{p.foto_principal ? <ProtectedImage src={p.foto_principal} alt="" className="h-full w-full object-cover" /> : <Package className="h-4 w-4 m-2.5 text-slate-700" />}</div>
                      <div><div className="text-slate-100">{p.descricao}</div><div className="font-mono text-xs text-sky-400">{p.pais_origem}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-400">{p.fornecedor?.nome || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-200">{num(p.quantidade_atual)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-300">{p.lead_time_dias}d</td>
                  <td className="px-4 py-2.5 text-center"><StatusBadge status={p.metrics.status_reposicao} /></td>
                  <td className="px-4 py-2.5">
                    <span className={`flex items-center gap-1 ${p.dias_ate_pedido != null && p.dias_ate_pedido < 20 ? "text-rose-300" : "text-slate-300"}`}>
                      <Clock className="h-3.5 w-3.5" /> {fmtDate(p.data_recomendada_pedido)} {p.dias_ate_pedido != null && <span className="text-xs text-slate-500">({p.dias_ate_pedido}d)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-sky-300 font-bold">{p.metrics.quantidade_sugerida}</td>
                  {data.finance && <td className="px-4 py-2.5 text-right font-mono text-emerald-300">{brl(p.metrics.custo_estimado)}</td>}
                </tr>
              ))}
              {data.itens.length === 0 && <tr><td colSpan={data.finance ? 8 : 7} className="px-4 py-10 text-center text-slate-500">Nenhum item importado requer pedido no momento.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
